import { useContext, useEffect, useRef, useState } from "react";
import { getData } from "../../Services/getData";
import { Vocabulary } from "../../Utils/Vocabulary";
import { CustomThemeContext } from "../../Context/CustomThemeProvider";
import styles from "../../Styles/previewPDF.module.css";
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  IconButton,
} from "@mui/material";
import { ZoomIn, ZoomOut } from "@mui/icons-material";
import {
  LazyDocumentSlotProps,
  LazyPdfDocumentGroupProps,
  PreviewDocumentProps,
} from "../../Utils/Types";
import { pdfjs, Document as PdfDocument, Page } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const DOCUMENT_LOAD_MARGIN = "600px 0px 600px 0px";

function LazyPdfDocumentGroup({ fileUrl, scale }: LazyPdfDocumentGroupProps) {
  const [numPages, setNumPages] = useState(0);

  return (
    <PdfDocument
      file={fileUrl}
      onLoadSuccess={({ numPages }) => setNumPages(numPages)}
    >
      {Array.from(new Array(numPages), (_, pageIndex) => (
        <Page
          key={`p-${pageIndex + 1}`}
          pageNumber={pageIndex + 1}
          scale={scale}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          className={styles.pdfPreviewPage}
        />
      ))}
    </PdfDocument>
  );
}

/**
 * Defers mounting a whole PDF document until it scrolls near the viewport,
 * so a module with several documents doesn't parse/render all of them upfront.
 */
function LazyDocumentSlot({
  fileUrl,
  scale,
  docIndex,
  isLoaded,
  setLoadedDocIndices,
}: LazyDocumentSlotProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLoaded) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLoadedDocIndices((prev) =>
            prev.has(docIndex) ? prev : new Set(prev).add(docIndex)
          );
        }
      },
      { rootMargin: DOCUMENT_LOAD_MARGIN }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isLoaded, docIndex, setLoadedDocIndices]);

  if (!isLoaded) {
    return (
      <div ref={ref} className={styles.loadingWrapper}>
        <CircularProgress color="secondary" />
      </div>
    );
  }

  return <LazyPdfDocumentGroup fileUrl={fileUrl} scale={scale} />;
}

function PreviewPDFDoc(props: PreviewDocumentProps) {
  const { urls, htmlContent, onClose } = props;
  const scaleAmount = 0.5;
  const themeContext: any = useContext(CustomThemeContext);
  const [state, setState] = useState({
    templateLoaded: 0,
    scale: 1.0,
    emailSent: false,
  });
  const [content, setContent] = useState(htmlContent);
  const [loadedDocIndices, setLoadedDocIndices] = useState<Set<number>>(
    new Set([0])
  );

  useEffect(() => {
    if (urls?.length) {
      const url = urls[0];
      getData(url).then((response: any) => {
        if (response?.errors) return;
        setState((prev) => ({ ...prev, templateLoaded: 1 }));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urls]);

  useEffect(() => {
    if (!content) {
      setContent(htmlContent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htmlContent]);

  const zoomIn = () => {
    setState((prev) => ({ ...prev, scale: prev.scale + scaleAmount }));
  };

  const zoomOut = () => {
    setState((prev) => ({ ...prev, scale: prev.scale - scaleAmount }));
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <DialogContent className={styles.dialogContent}>
        {urls?.length ? (
          !state.templateLoaded ? (
            <div className={styles.loadingWrapper}>
              <CircularProgress color="secondary" />
            </div>
          ) : (
            urls.map((pdfUrl, idx) => (
              <LazyDocumentSlot
                key={idx}
                fileUrl={pdfUrl}
                scale={state.scale}
                docIndex={idx}
                isLoaded={loadedDocIndices.has(idx)}
                setLoadedDocIndices={setLoadedDocIndices}
              />
            ))
          )
        ) : (
          Vocabulary.loading
        )}
      </DialogContent>
      <DialogActions classes={{ root: styles.actionsWrapper }}>
        <IconButton disabled={state.scale <= 0.3} onClick={zoomOut}>
          <ZoomOut />
        </IconButton>
        <IconButton disabled={state.scale >= 2} onClick={zoomIn}>
          <ZoomIn />
        </IconButton>
        <Button
          onClick={() => onClose(state.emailSent)}
          style={{ background: themeContext.theme?.palette.cancel?.main }}
        >
          {Vocabulary.close}
        </Button>
      </DialogActions>
    </div>
  );
}

export default PreviewPDFDoc;
